import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import MangaCard from "@/components/feature/MangaCard";
import { categories } from "@/mocks/categories";
import { mangaList } from "@/mocks/manga";

export default function CategoryPage() {
  const { id } = useParams<{ id: string }>();
  const category = categories.find((c) => c.id === id);
  const list = category ? mangaList.filter((m) => m.categoryId === category.id) : [];
  const others = categories.filter((c) => c.id !== id);

  const schema = category
    ? {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `${category.name}恋爱漫画`,
        description: `${category.name}恋爱漫画专区，${category.desc}`,
        url: `https://emovibe1.github.io/category/${category.id}`,
        inLanguage: "zh-CN",
        mainEntity: {
          "@type": "ItemList",
          itemListElement: list.map((m, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: m.title,
            url: `https://emovibe1.github.io/detail/${m.id}`,
          })),
        },
      }
    : null;

  return (
    <>
      <Navbar />
      {schema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      )}
      <main className="pt-16 md:pt-[72px]">
        {/* 栏目头 */}
        <section className="border-b border-background-200/70 bg-background-100/60">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:py-12">
            <nav aria-label="面包屑" className="flex items-center gap-1.5 text-sm text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800">{category ? category.name : "栏目"}</span>
            </nav>

            <div className="mt-5 flex flex-col gap-5 md:flex-row md:items-center">
              {category && (
                <img
                  src={category.cover}
                  alt={`${category.name}恋爱漫画专区封面`}
                  className="h-28 w-40 rounded-2xl object-cover object-top md:h-32 md:w-48"
                />
              )}
              <div>
                <h1 className="font-display text-3xl text-foreground-950 md:text-4xl">
                  {category ? category.name : "未找到栏目"}
                </h1>
                {category && (
                  <>
                    <p className="mt-1 text-sm font-medium" style={{ color: category.accent }}>
                      {category.en} · {category.tagline}
                    </p>
                    <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-foreground-600">{category.desc}</p>
                  </>
                )}
              </div>
              <span className="md:ml-auto shrink-0 rounded-full bg-primary-100 px-4 py-2 text-sm font-medium text-primary-700">
                {list.length} 部精选作品
              </span>
            </div>
          </div>
        </section>

        {/* 作品列表 */}
        <section className="mx-auto w-full max-w-[1400px] px-4 py-10 md:py-14">
          {list.length > 0 ? (
            <>
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-500 text-background-50">
                  <i className="ri-book-2-fill text-xs"></i>
                </span>
                <h2 className="text-lg font-bold text-foreground-950">本栏目作品</h2>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                {list.map((manga) => (
                  <MangaCard key={manga.id} manga={manga} />
                ))}
              </div>
            </>
          ) : (
            <p className="py-16 text-center text-foreground-500">这个栏目暂时还没有作品，去看看其他栏目吧。</p>
          )}
        </section>

        {/* 其他栏目 */}
        <section className="bg-background-100/60 py-10 md:py-14">
          <div className="mx-auto w-full max-w-[1400px] px-4">
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                <i className="ri-compass-3-line text-xs"></i>
              </span>
              <h2 className="text-lg font-bold text-foreground-950">探索其他栏目</h2>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {others.map((c) => {
                const count = mangaList.filter((m) => m.categoryId === c.id).length;
                return (
                  <Link
                    key={c.id}
                    to={`/category/${c.id}`}
                    className="group relative overflow-hidden rounded-2xl border border-background-200/70 bg-background-50"
                  >
                    <div className="relative aspect-[16/10] w-full overflow-hidden">
                      <img
                        src={c.cover}
                        alt={`${c.name}恋爱漫画栏目`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 to-transparent"></div>
                      <span className="absolute inset-x-3 bottom-3 text-background-50">
                        <span className="block font-display text-xl">{c.name}</span>
                        <span className="text-xs text-background-100/90">{count} 部作品</span>
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}